import {Injectable} from '@angular/core';
import {environment} from '../../environments/environment';
import {HttpClient, HttpParams} from '@angular/common/http';
import {map, Observable, take} from 'rxjs';
import {Notification} from '../_types/notification';
import {fetchAllPages} from '../_utils/http-params';
import {Community} from '../_types/communities';
import {LocalStorageService} from './local-storage.service';
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {CommentsComponent} from "../shared/comments/comments.component";
import {BadgesComponent} from "../shared/badges/badges.component";
import {TextLightboxComponent} from "../shared/text-lightbox/text-lightbox.component";
import {Router} from "@angular/router";
import {ContentService} from "./content.service";
import {SnackbarService} from "./snackbar.service";

@Injectable({
    providedIn: 'root'
})
export class NotificationService {
    private api: string = `${environment.apiUrl}/notifications`;

    constructor(
        private http: HttpClient,
        private localStorageService: LocalStorageService,
        public dialog: MatDialog,
        private readonly router: Router,
        private readonly contentService: ContentService,
        private snack: SnackbarService,
    ) {
    }

    getNotifications(unread?: boolean): Observable<Notification[]> {
        let parameters: HttpParams = new HttpParams();
        if (unread) {
            parameters = parameters.set('unread_only', true);
        }
        const community: Community | null = this.localStorageService.getItem<Community | null>('lastSelectedCommunity', null);
        return fetchAllPages<Notification>(this.http, `${this.api}/`, 1000, parameters).pipe(
            map(notifications => notifications.filter(notif => notif.community === community?.id))
        );
    }

    getNotification(notificationId: number): Observable<Notification> {
        return this.http.get<Notification>(`${this.api}/${notificationId}/`);
    }

    markNotificationAsRead(notificationId: number): Observable<Notification> {
        return this.http.patch<Notification>(`${this.api}/${notificationId}/`, {is_read: true});
    }

    markAllNotificationsAsRead(): Observable<void> {
        return this.http.get<void>(`${this.api}/mark-all-as-read/`);
    }


    notificationRedirect(notification: Notification): void {
        let post: string | undefined;
        let comment: string | undefined;
        switch (notification.notification_type) {
            case "comment_flagged":
            case "comment_liked":
            case "comment_disliked":
            case "comment_needs_moderation":
            case "new_reply":
            case "new_comment":
                ({post, comment} = this.extractUrlParams(notification.action_url));
                if (!post || !comment) break;
                this.router.navigate([], {
                    queryParams: {comment: comment},
                    queryParamsHandling: 'merge',
                    replaceUrl: true,
                });
                this.contentService.getPostById(+post).pipe(take(1))
                    .subscribe(post => {
                        this.contentService.getPostComments(post.id).pipe(take(1)).subscribe((comments) => {
                            const dialogRef: MatDialogRef<CommentsComponent> = this.dialog.open(CommentsComponent, {
                                data: {
                                    comments: comments,
                                    post,
                                    commentForScroll: comment
                                },
                                autoFocus: false,
                                panelClass: ['mediumWindow', 'w-100', 'comments-modal'],
                            });
                        });
                    }, error => {
                        this.snack.openErrorSnackBar('Sorry the content you are looking for no longer exists')
                    });
                break;
            case "post_flagged":
            case "post_needs_moderation":
            case "post_disliked":
            case "post_liked":
                ({post} = this.extractUrlParams(notification.action_url));
                if (!post) break;
                this.contentService.getPostById(+post).pipe(take(1))
                    .subscribe(post => {
                        const dialogRef: MatDialogRef<CommentsComponent> = this.dialog.open(CommentsComponent, {
                            data: {
                                post,
                            },
                            autoFocus: false,
                            panelClass: ['mediumWindow', 'w-100', 'comments-modal'],
                        });
                    }, error => {
                        this.snack.openErrorSnackBar('Sorry the content you are looking for no longer exists')
                    });
                break;
            case "badge_earned":
                this.dialog.open(BadgesComponent, {panelClass: 'mediumWindow', maxHeight: '80vh'});
                break;
            case "feedback_submission":
                let that = this
                this.router.navigate(['/admin/feedback-messages']).then(function () {
                    that.dialog.closeAll();
                })
                break;
            default:
                this.dialog.open(TextLightboxComponent, {
                    panelClass: 'mediumWindow',
                    maxHeight: '80vh',
                    data: {
                        title: notification.title,
                        content: notification.content
                    }
                });
                break;
        }
    }

    private extractUrlParams(url: string) {
        const postRegex = /\/posts\/(\d+)/;
        const commentRegex = /\/comments\/(\d+)/;

        const postMatch: RegExpMatchArray | null = url.match(postRegex);
        const commentMatch: RegExpMatchArray | null = url.match(commentRegex);

        const post: string | undefined = postMatch ? postMatch[1] : undefined;
        const comment: string | undefined = commentMatch ? commentMatch[1] : undefined;

        return {post, comment};
    }

}
